(ns practitest.frontend.batch-modal.events
  (:require
    [ajax.core :as ajax]
    [clojure.string :as str]
    [day8.re-frame.http-fx]
    [practitest.frontend.common-entities :as common-entities]
    [practitest.frontend.common-entities.metadata :as metadata]
    [practitest.frontend.common.i18n :refer [format-translation get-translation]]
    [practitest.frontend.common.mixpanel :as mixpanel]
    [practitest.frontend.components.grid.localstorage :as grid-storage]
    [practitest.frontend.components.notifications :as notifications]
    [practitest.frontend.routing.backend-routes :as broutes]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]))

;; Shared shell for batch operations on a grid selection. `:mode` distinguishes
;; the operation (:edit today; :clone is a planned near-future addition reusing
;; this same modal/events/subs).

(def default-state
  {:comment                 ""
   :disable-notifications?  true
   ;; {field-id {:checked? bool :value any}} - unchecked until the user opts a
   ;; field into the batch; :value may start from a common-values prefill (see
   ;; below) but that alone never checks the field.
   :field-values            {}
   :preserve-tags?          false
   ;; {field-id "error message"} - only for fields the user checked and left
   ;; empty; populated on Update click, cleared as each field is fixed.
   :field-errors            {}
   ;; {:system {...} :custom {...}} raw values shared by every selected entity.
   :common-values           {}})

;; Same sentinel prepare-full-selection-ids already builds for the legacy
;; (non-modal) batch-navigate! flow - MultipleEntities#batch_selector needs
;; "_all"/"_cv:<id>"/"_f:<id>"/"_auto_filter:..." to know which view/folder a
;; :_all selection is actually scoped to, not just "every entity of this type".
(defn selection-params [selection current-view grid-filter]
  (cond-> {:ids (common-entities/prepare-full-selection-ids selection current-view)}
    (= selection :_all) (assoc :filter grid-filter)))

(re-frame/reg-event-fx
  ::open
  (fn [{:keys [db]} [_ {:keys [entity selection current-view state-key mode set-id optional-params]
                        :or   {mode :edit}}]]
    (mixpanel/track "Batch Edit"
      {"Action" "Batch Edit Opened" "Entity" (mixpanel/entity-type->mixpanel entity)})
    (let [grid-filter (:filter optional-params)
          route-name (get-in metadata/entities-metadata [entity :urls :batch-common-values])]
      {:db (assoc db :batch-modal
                  (assoc default-state
                         :open? true
                         :mode mode
                         :entity entity
                         :selection selection
                         :current-view current-view
                         :state-key state-key
                         :filter grid-filter
                         :set-id set-id))
       :http-xhrio
       {:method          :get
        :uri             (broutes/get-url route-name {:p_id (subs/get-current-project-id db)})
        :format          :url
        :vec-strategy    :rails
        :params          (cond-> (selection-params selection current-view grid-filter)
                           set-id (assoc :set_id set-id))
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success      [::common-values-loaded]
        ;; A failed fetch just means no prefill - not worth its own error toast.
        :on-failure      [::common-values-loaded]}})))

(re-frame/reg-event-db
  ::common-values-loaded
  (fn [db [_ response]]
    (assoc-in db [:batch-modal :common-values] response)))

(re-frame/reg-event-db
  ::close
  (fn [db [_]]
    (assoc-in db [:batch-modal :open?] false)))

(re-frame/reg-event-db
  ::set-comment
  (fn [db [_ text]]
    (assoc-in db [:batch-modal :comment] text)))

(re-frame/reg-event-db
  ::toggle-disable-notifications
  (fn [db [_]]
    (update-in db [:batch-modal :disable-notifications?] not)))

(re-frame/reg-event-db
  ::set-field-value
  (fn [db [_ field-id value]]
    (-> db
        (update-in [:batch-modal :field-values field-id] assoc :checked? true :value value)
        (update-in [:batch-modal :field-errors] dissoc field-id))))

;; Used when a linkedlist field's parent value changes - its own previously
;; picked value is no longer valid for the new set of options. Doesn't touch
;; :checked?, unlike set-field-value - clearing a child's value shouldn't
;; silently opt it into (or out of) the batch.
(re-frame/reg-event-db
  ::clear-field-value
  (fn [db [_ field-id]]
    (update-in db [:batch-modal :field-values field-id] assoc :value nil)))

(re-frame/reg-event-db
  ::set-field-checked
  (fn [db [_ field-id checked?]]
    (cond-> (update-in db [:batch-modal :field-values field-id] assoc :checked? checked?)
      (not checked?) (update-in [:batch-modal :field-errors] dissoc field-id))))

(re-frame/reg-event-db
  ::set-field-errors
  (fn [db [_ errors]]
    (assoc-in db [:batch-modal :field-errors] errors)))

(re-frame/reg-event-db
  ::toggle-preserve-tags
  (fn [db [_]]
    (update-in db [:batch-modal :preserve-tags?] not)))

;; The qtest_design param key in fields.cljs's build-submit-params is still
;; Tests-specific; generalizing that to the other batch-editable entities is
;; a separate follow-up from the route lookup below.
(re-frame/reg-event-fx
  ::submit
  (fn [{:keys [db]} [_ params]]
    (let [entity (get-in db [:batch-modal :entity])
          route-name (get-in metadata/entities-metadata [entity :urls :batch-edit-apply])]
      {:db (assoc-in db [:batch-modal :submitting?] true)
       :http-xhrio
       {:method          :post
        :uri             (broutes/get-url route-name {:p_id (subs/get-current-project-id db)})
        :params          params
        :format          (ajax/json-request-format)
        :vec-strategy    :rails
        :response-format (ajax/json-response-format {:keywords? true})
        :on-success      [::submit-success]
        :on-failure      [::submit-error]}})))

(defn- refresh-grid-fx [db]
  (let [entity (get-in db [:batch-modal :entity])
        state-key (get-in db [:batch-modal :state-key])]
    ;; Instances have no flat, top-level grid/list route of their own - they're
    ;; always nested inside a Test Set, refetched via its own dedicated event.
    (if (= entity :instance)
      [:practitest.frontend.testset2.events/fetch-instances]
      (let [table-id (get-in metadata/entities-metadata [entity :table-id])
            storage-key (str (subs/get-current-project-id db) "-" table-id)
            per-page (or (grid-storage/load-per-page storage-key) 10)
            current-page (or (grid-storage/load-current-page storage-key) 1)]
        [:practitest.frontend.common-entities.events/fetch-entity-list entity state-key
         {:per_page per-page :page current-page}]))))

(defn- errors-message [errors locked-count]
  (str/join "<br>"
    (cond-> (mapv (fn [{:keys [display_id messages]}]
                    (str display_id ": " (str/join ", " messages)))
                  errors)
      (pos? locked-count)
      (conj (format-translation
              (get-translation "batch-modal.submit.locked-entities"
                               "%{count} entities were not updated due to workflow policy restrictions.")
              {:count locked-count})))))

(re-frame/reg-event-fx
  ::submit-success
  (fn [{:keys [db]} [_ response]]
    (let [entity (get-in db [:batch-modal :entity])
          {:keys [status message errors locked_count]} response]
      (when (= status "ok")
        (mixpanel/track "Batch Edit"
          {"Action" "Update Succeeded" "Entity" (mixpanel/entity-type->mixpanel entity)}))
      (cond-> {:db (-> db
                      (assoc-in [:batch-modal :submitting?] false)
                      (assoc-in [:batch-modal :open?] false))
               :dispatch-n (cond-> [(refresh-grid-fx db)]
                             (= status "ok")
                             (conj [::notifications/success2 message {:auto-close true}])

                             (= status "error")
                             (conj [::notifications/error2
                                    (errors-message errors (or locked_count 0))
                                    {:raw-html? true :auto-close false}]))}
        (= status "ok")
        (assoc :practitest.frontend.global-fields-settings.events/dispatch-browser-event
               "pt:entities-updated")))))

(re-frame/reg-event-fx
  ::submit-error
  (fn [{:keys [db]} [_ error]]
    (let [message (or (get-in error [:response :message])
                      (get-translation "batch-modal.submit.error" "Error updating entities"))]
      {:db (assoc-in db [:batch-modal :submitting?] false)
       :dispatch [::notifications/error2 message {:auto-close false}]})))
